import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toursAPI } from '../services/api';

interface TourContextType {
  completedTours: Set<string>;
  activeTour: string | null;
  startTour: (tourId: string) => void;
  completeTour: (tourId: string) => void;
  isTourComplete: (tourId: string) => boolean;
}

const TourContext = createContext<TourContextType>({
  completedTours: new Set(),
  activeTour: null,
  startTour: () => {},
  completeTour: () => {},
  isTourComplete: () => false,
});

export const useTour = () => useContext(TourContext);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [completedTours, setCompletedTours] = useState<Set<string>>(new Set());
  const [activeTour, setActiveTour] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setCompletedTours(new Set());
      initialLoadDone.current = false;
      return;
    }

    const fetchProgress = async () => {
      try {
        const res = await toursAPI.progress();
        const tours = Array.isArray(res.data) ? res.data : res.data.completed_tours || [];
        const completed = new Set<string>(tours.map((t: any) => (typeof t === 'string' ? t : t.tour_id)));
        setCompletedTours(completed);

        // Auto-trigger design-sessions tour if not completed (first time only)
        if (!initialLoadDone.current && !completed.has('design-sessions')) {
          setTimeout(() => setActiveTour('design-sessions'), 1200);
        }
        initialLoadDone.current = true;
      } catch {
        // tours endpoint may not exist yet — fail silently
        initialLoadDone.current = true;
      }
    };

    fetchProgress();
  }, [isAuthenticated]);

  const startTour = useCallback((tourId: string) => {
    setActiveTour(tourId);
  }, []);

  const completeTour = useCallback(async (tourId: string) => {
    setActiveTour(null);
    setCompletedTours((prev) => {
      const next = new Set(prev);
      next.add(tourId);
      return next;
    });
    try {
      await toursAPI.complete(tourId);
    } catch {
      // fail silently — completion is best-effort
    }
  }, []);

  const isTourComplete = useCallback(
    (tourId: string) => completedTours.has(tourId),
    [completedTours]
  );

  return (
    <TourContext.Provider
      value={{
        completedTours,
        activeTour,
        startTour,
        completeTour,
        isTourComplete,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};
